// =============================================================================
// ui/charts/Gauge.tsx — the arc gauge.
// -----------------------------------------------------------------------------
// Relocated from the old client's `dashArcGauge`, arc geometry unchanged: a 270°
// sweep starting at 135°, which puts the gap at the BOTTOM and the two ends
// symmetric about vertical.
//
// ⚠ THE SWEEP IS 270°, SO THE `large-arc-flag` MATTERS. An SVG arc with a sweep
// over 180° needs `large-arc = 1` or the renderer draws the SHORT way round —
// a 75% gauge would render as a 25% one, in the correct colour, with the correct
// centre text. It is computed per-call from the actual angle rather than pinned,
// because the value arc is short at low percentages and long at high ones.
//
// ⚠ NO WIDTH/HEIGHT ATTRIBUTES. The gauge is sized by its CSS container and the
// viewBox preserves the aspect ratio, so `preserveAspectRatio="xMidYMid meet"`
// keeps the arc centred and uncropped when the container is not square. This is
// the OPPOSITE of the equity curve, which stretches — see that file.
// =============================================================================

const SIZE = 128;
const START = 135;
const SWEEP = 270;

const polar = (angleDeg: number, r: number, c: number): [number, number] => {
  const rad = (angleDeg * Math.PI) / 180;
  return [c + r * Math.cos(rad), c + r * Math.sin(rad)];
};

/** An arc from `fromPct` to `toPct` of the sweep, both 0–1. */
function arcPath(fromPct: number, toPct: number, r: number, c: number): string {
  const a0 = START + SWEEP * fromPct;
  const a1 = START + SWEEP * toPct;
  const [x0, y0] = polar(a0, r, c);
  const [x1, y1] = polar(a1, r, c);
  // ⚠ See the header. Without this a 3/4-full gauge renders 1/4 full.
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

export interface GaugeProps {
  /** 0–100. Already clamped by the domain — this does not re-clamp silently. */
  pct: number;
  /** The centre text, pre-formatted: `43%`, `0.29R`, `∞`, `—`. */
  text: string;
  /** A CSS colour or `var(--…)`. */
  color: string;
  label: string;
}

export function Gauge({ pct, text, color, label }: GaugeProps) {
  const r = SIZE / 2 - 12;
  const c = SIZE / 2;
  const frac = Math.max(0, Math.min(1, (pct || 0) / 100));

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE - 4}`}
      preserveAspectRatio="xMidYMid meet"
      className="gauge-svg"
      role="img"
      // ⚠ The value AND the label, because the centre text alone ("43%") does
      // not say what is 43%. Six of these sit in a grid.
      aria-label={`${label}: ${text}`}
    >
      {/* ⚠ `--bg-4`, matching beta (index.html:27227). `--bg-3` (#1c2128) against
          the `--bg-2` (#161b22) card is about six levels of separation — the
          track all but vanishes, and a 12% dial reads as a floating stub with no
          ring behind it to size it against. The unfilled part of the arc is the
          denominator; it has to be visible. See #49. */}
      <path d={arcPath(0, 1, r, c)} fill="none" stroke="var(--bg-4)" strokeWidth={9} strokeLinecap="round" />
      {/* ⚠ Suppressed below 0.1%, not below 0%. A rounded-cap arc of zero length
          still paints a visible dot at the start angle, so a 0% gauge would show
          a stub that reads as a small positive value. */}
      {frac > 0.001 ? (
        <path d={arcPath(0, frac, r, c)} fill="none" stroke={color} strokeWidth={9} strokeLinecap="round" />
      ) : null}
      <text
        x={c} y={c + 4} textAnchor="middle"
        fontFamily="var(--mono)" fontSize={24} fontWeight={600} fill="var(--text)"
      >
        {text}
      </text>
    </svg>
  );
}
